import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { getSalesDashboard } from '../api/salesReportsApi';
import { formatDateDDMMYYYY } from '../utils/dateFormat';
import styles from './SalesReportsView.module.css';

const translations = {
  en: {
    pageTitle: 'My Sales Reports',
    homepage: 'Homepage',
    reportScope: 'Personal report',
    reportSubtitle: 'Your own requests, quantities, sales values, customers, products, and fulfillment for the selected period.',
    period: 'Period',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly',
    custom: 'Custom',
    from: 'From',
    to: 'To',
    netSales: 'Net sales',
    grossSales: 'Gross sales',
    approvedDiscounts: 'Approved discounts',
    totalQuantity: 'Total quantity',
    outboundSales: 'Outbound sales',
    inboundReturns: 'Inbound returns',
    requests: 'Requests',
    fulfilled: 'Fulfilled',
    openRequests: 'Open requests',
    customers: 'Customers',
    products: 'Products',
    avgOrderValue: 'Avg request value',
    avgQuantity: 'Avg quantity / request',
    calculationTitle: 'Calculation',
    grossFormula: 'Gross = product line totals before approved discounts.',
    netFormula: 'Net = gross minus approved discounts.',
    outboundFormula: 'Outbound is the value and quantity removed from inventory for sales orders.',
    inboundFormula: 'Inbound is the value and quantity added back through return/add requests.',
    productPerformance: 'Product performance',
    customerPerformance: 'Customer performance',
    statusBreakdown: 'Request status',
    movementBreakdown: 'Inbound / outbound',
    releaseBreakdown: 'Release state',
    product: 'Product',
    sku: 'SKU',
    customer: 'Customer',
    customerId: 'Customer ID',
    quantity: 'Qty',
    gross: 'Gross',
    discount: 'Discount',
    net: 'Net',
    value: 'Value',
    status: 'Status',
    count: 'Count',
    share: 'Share',
    lastActivity: 'Last activity',
    lastRequest: 'Last request',
    noData: 'No report data for this period.',
    loading: 'Loading reports...',
    failedLoad: 'Failed to load reports.',
    notAvailable: '-',
    pending: 'Pending',
    accepted: 'Accepted',
    rejected: 'Rejected',
    held: 'Held',
    cancelled: 'Cancelled',
    dateChanged: 'Date changed',
    reserved: 'Reserved',
    released: 'Released',
    unheld: 'Unheld',
    inbound: 'Inbound',
    outbound: 'Outbound',
  },
  ar: {
    pageTitle: 'تقارير مبيعاتي',
    homepage: 'الرئيسية',
    reportScope: 'تقرير شخصي',
    reportSubtitle: 'طلباتك وكمياتك وقيم المبيعات والعملاء والمنتجات ونسبة الإنجاز خلال الفترة المختارة.',
    period: 'الفترة',
    daily: 'يومي',
    weekly: 'أسبوعي',
    monthly: 'شهري',
    yearly: 'سنوي',
    custom: 'مخصص',
    from: 'من',
    to: 'إلى',
    netSales: 'صافي المبيعات',
    grossSales: 'إجمالي المبيعات',
    approvedDiscounts: 'الخصومات المعتمدة',
    totalQuantity: 'إجمالي الكمية',
    outboundSales: 'المبيعات الصادرة',
    inboundReturns: 'الوارد / المرتجعات',
    requests: 'الطلبات',
    fulfilled: 'منجز',
    openRequests: 'طلبات مفتوحة',
    customers: 'العملاء',
    products: 'المنتجات',
    avgOrderValue: 'متوسط قيمة الطلب',
    avgQuantity: 'متوسط الكمية / طلب',
    calculationTitle: 'طريقة الحساب',
    grossFormula: 'الإجمالي = مجموع بنود المنتجات قبل الخصومات المعتمدة.',
    netFormula: 'الصافي = الإجمالي ناقص الخصومات المعتمدة.',
    outboundFormula: 'الصادر هو قيمة وكمية المنتجات الخارجة من المخزون لطلبات البيع.',
    inboundFormula: 'الوارد هو قيمة وكمية المنتجات المضافة مرة أخرى من طلبات الإضافة أو المرتجعات.',
    productPerformance: 'أداء المنتجات',
    customerPerformance: 'أداء العملاء',
    statusBreakdown: 'حالة الطلبات',
    movementBreakdown: 'الوارد / الصادر',
    releaseBreakdown: 'حالة الصرف',
    product: 'المنتج',
    sku: 'SKU',
    customer: 'العميل',
    customerId: 'رقم العميل',
    quantity: 'الكمية',
    gross: 'الإجمالي',
    discount: 'الخصم',
    net: 'الصافي',
    value: 'القيمة',
    status: 'الحالة',
    count: 'العدد',
    share: 'النسبة',
    lastActivity: 'آخر نشاط',
    lastRequest: 'آخر طلب',
    noData: 'لا توجد بيانات لهذه الفترة.',
    loading: 'جار تحميل التقارير...',
    failedLoad: 'فشل تحميل التقارير.',
    notAvailable: '-',
    pending: 'قيد الانتظار',
    accepted: 'مقبول',
    rejected: 'مرفوض',
    held: 'معلق',
    cancelled: 'ملغى',
    dateChanged: 'تم تغيير التاريخ',
    reserved: 'محجوز',
    released: 'تم الصرف',
    unheld: 'غير محجوز',
    inbound: 'وارد',
    outbound: 'صادر',
  },
};

const toInputDate = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return value.toISOString().slice(0, 10);
};

const getRangeForPeriod = (period) => {
  const end = new Date();
  const start = new Date(end);

  if (period === 'daily') {
    start.setHours(0, 0, 0, 0);
  } else if (period === 'weekly') {
    start.setDate(end.getDate() - 6);
  } else if (period === 'monthly') {
    start.setMonth(end.getMonth() - 1);
  } else if (period === 'yearly') {
    start.setFullYear(end.getFullYear() - 1);
  }

  return { startDate: toInputDate(start), endDate: toInputDate(end) };
};

const getValidNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const SalesReportsView = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key] || translations.en[key] || key;
  const [period, setPeriod] = useState('monthly');
  const initialRange = useMemo(() => getRangeForPeriod('monthly'), []);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  const formatNumber = (value, maximumFractionDigits = 0) =>
    getValidNumber(value).toLocaleString(locale, { maximumFractionDigits });

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 2,
    }).format(getValidNumber(value));

  const formatPercent = (value) =>
    `${(getValidNumber(value) * 100).toLocaleString(locale, { maximumFractionDigits: 1 })}%`;

  const formatDate = (value) => {
    return formatDateDDMMYYYY(value, t('notAvailable'));
  };

  const labelStatus = (status) => {
    const labels = {
      pending: t('pending'),
      accepted: t('accepted'),
      rejected: t('rejected'),
      held: t('held'),
      cancelled: t('cancelled'),
      'date-changed': t('dateChanged'),
      reserved: t('reserved'),
      released: t('released'),
      unheld: t('unheld'),
      add: t('inbound'),
      remove: t('outbound'),
    };
    return labels[status] || status || t('notAvailable');
  };

  const applyPeriod = (nextPeriod) => {
    setPeriod(nextPeriod);
    if (nextPeriod === 'custom') return;
    const range = getRangeForPeriod(nextPeriod);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const updateManualDate = (field, value) => {
    setPeriod('custom');
    if (field === 'startDate') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await getSalesDashboard({ startDate, endDate });
        setReport(data);
      } catch (err) {
        console.error('Error loading sales reports:', err);
        setError(translations[language].failedLoad);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [endDate, language, startDate]);

  const summary = report?.summary || {};
  const products = Array.isArray(report?.productPerformance)
    ? report.productPerformance
    : [];
  const customers = Array.isArray(report?.customerPerformance)
    ? report.customerPerformance
    : [];
  const actions = Array.isArray(summary.actionBreakdown)
    ? summary.actionBreakdown
    : [];
  const statuses = Array.isArray(summary.statusBreakdown)
    ? summary.statusBreakdown
    : [];
  const releases = Array.isArray(summary.releaseBreakdown)
    ? summary.releaseBreakdown
    : [];

  const summaryCards = [
    {
      label: t('grossSales'),
      value: formatCurrency(summary.totalGrossValue),
      meta: `${t('requests')}: ${formatNumber(summary.totalRequests)}`,
    },
    {
      label: t('approvedDiscounts'),
      value: formatCurrency(summary.totalDiscountValue),
      meta: `${t('net')}: ${formatCurrency(summary.totalNetValue)}`,
    },
    {
      label: t('totalQuantity'),
      value: formatNumber(summary.totalQuantity),
      meta: `${t('avgQuantity')}: ${formatNumber(summary.averageQuantityPerRequest, 2)}`,
    },
    {
      label: t('fulfilled'),
      value: formatPercent(summary.fulfillmentRate),
      meta: `${t('openRequests')}: ${formatNumber(summary.openRequests)}`,
    },
    {
      label: t('customers'),
      value: formatNumber(summary.totalCustomers),
      meta: `${t('products')}: ${formatNumber(summary.totalProducts)}`,
    },
    {
      label: t('avgOrderValue'),
      value: formatCurrency(summary.averageValuePerRequest),
      meta: `${formatDate(summary.firstRequestAt)} - ${formatDate(summary.lastRequestAt)}`,
    },
  ];

  const renderBreakdownTable = (title, rows) => (
    <article className={styles.tableCard}>
      <div className={styles.tableHeader}>
        <h2>{title}</h2>
      </div>
      <div className={styles.tableScroller}>
        <table className={styles.breakdownTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('status')}</th>
              <th>{t('count')}</th>
              <th>{t('share')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} className={styles.emptyCell}>{t('noData')}</td></tr>
            ) : (
              rows.map((item, index) => (
                <tr key={item.key}>
                  <td>{index + 1}</td>
                  <td><span className={styles.badge}>{labelStatus(item.key)}</span></td>
                  <td>{formatNumber(item.count)}</td>
                  <td>{formatPercent(item.share)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </article>
  );

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      showHome
      homeLabel={t('homepage')}
      onHome={() => navigate('/sales')}
      contentClassName={styles.page}
    >
      <section className={styles.reportIntro}>
        <div>
          <span>{t('reportScope')}</span>
          <p>{t('reportSubtitle')}</p>
        </div>
        <strong>{formatDate(startDate)} - {formatDate(endDate)}</strong>
      </section>

      <section className={styles.filtersPanel}>
        <label>
          <span>{t('period')}</span>
          <select value={period} onChange={(event) => applyPeriod(event.target.value)}>
            <option value="daily">{t('daily')}</option>
            <option value="weekly">{t('weekly')}</option>
            <option value="monthly">{t('monthly')}</option>
            <option value="yearly">{t('yearly')}</option>
            <option value="custom">{t('custom')}</option>
          </select>
        </label>
        <label>
          <span>{t('from')}</span>
          <input type="date" value={startDate} onChange={(event) => updateManualDate('startDate', event.target.value)} />
        </label>
        <label>
          <span>{t('to')}</span>
          <input type="date" value={endDate} onChange={(event) => updateManualDate('endDate', event.target.value)} />
        </label>
      </section>

      {loading ? (
        <div className={styles.state}>{t('loading')}</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : (
        <>
          <section className={styles.snapshotPanel}>
            <div className={styles.primaryMetric}>
              <span>{t('netSales')}</span>
              <strong>{formatCurrency(summary.totalNetValue)}</strong>
              <small>{t('grossSales')}: {formatCurrency(summary.totalGrossValue)}</small>
            </div>
            <div className={styles.movementMetrics}>
              <div>
                <span>{t('outboundSales')}</span>
                <strong>{formatCurrency(summary.outboundValue)}</strong>
                <small>{formatNumber(summary.outboundQuantity)} {t('quantity')}</small>
              </div>
              <div>
                <span>{t('inboundReturns')}</span>
                <strong>{formatCurrency(summary.inboundValue)}</strong>
                <small>{formatNumber(summary.inboundQuantity)} {t('quantity')}</small>
              </div>
            </div>
            <div className={styles.calculationBox}>
              <strong>{t('calculationTitle')}</strong>
              <ul>
                <li>{t('grossFormula')}</li>
                <li>{t('netFormula')}</li>
                <li>{t('outboundFormula')}</li>
                <li>{t('inboundFormula')}</li>
              </ul>
            </div>
          </section>

          <section className={styles.summaryGrid}>
            {summaryCards.map((card) => (
              <article key={card.label}>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.meta}</small>
              </article>
            ))}
          </section>

          <section className={styles.reportsGrid}>
            <article className={`${styles.tableCard} ${styles.wideCard}`}>
              <div className={styles.tableHeader}>
                <h2>{t('productPerformance')}</h2>
              </div>
              <div className={styles.tableScroller}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('product')}</th>
                      <th>{t('sku')}</th>
                      <th>{t('requests')}</th>
                      <th>{t('quantity')}</th>
                      <th>{t('gross')}</th>
                      <th>{t('discount')}</th>
                      <th>{t('net')}</th>
                      <th>{t('customers')}</th>
                      <th>{t('lastActivity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr><td colSpan={10} className={styles.emptyCell}>{t('noData')}</td></tr>
                    ) : (
                      products.map((item, index) => (
                        <tr key={item.product}>
                          <td>{index + 1}</td>
                          <td>
                            <div className={styles.stackedCell}>
                              <strong>{item.productName || item.product}</strong>
                              <span>{item.size || t('notAvailable')}</span>
                            </div>
                          </td>
                          <td>{item.product}</td>
                          <td>{formatNumber(item.requestCount)}</td>
                          <td>{formatNumber(item.totalQuantity)}</td>
                          <td>{formatCurrency(item.totalGrossValue)}</td>
                          <td>{formatCurrency(item.totalDiscountValue)}</td>
                          <td className={styles.positiveText}>{formatCurrency(item.totalNetValue)}</td>
                          <td>{formatNumber(item.customersInvolved)}</td>
                          <td>{formatDate(item.lastActivityAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className={`${styles.tableCard} ${styles.wideCard}`}>
              <div className={styles.tableHeader}>
                <h2>{t('customerPerformance')}</h2>
              </div>
              <div className={styles.tableScroller}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('customer')}</th>
                      <th>{t('customerId')}</th>
                      <th>{t('requests')}</th>
                      <th>{t('quantity')}</th>
                      <th>{t('gross')}</th>
                      <th>{t('discount')}</th>
                      <th>{t('net')}</th>
                      <th>{t('products')}</th>
                      <th>{t('lastRequest')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr><td colSpan={10} className={styles.emptyCell}>{t('noData')}</td></tr>
                    ) : (
                      customers.map((item, index) => (
                        <tr key={item.customerId}>
                          <td>{index + 1}</td>
                          <td><strong className={styles.customerName}>{item.name || t('notAvailable')}</strong></td>
                          <td>{item.customerCode || t('notAvailable')}</td>
                          <td>{formatNumber(item.requestCount)}</td>
                          <td>{formatNumber(item.totalQuantity)}</td>
                          <td>{formatCurrency(item.totalGrossValue)}</td>
                          <td>{formatCurrency(item.totalDiscountValue)}</td>
                          <td className={styles.positiveText}>{formatCurrency(item.totalNetValue)}</td>
                          <td>{formatNumber(item.productsTouched)}</td>
                          <td>{formatDate(item.lastRequestAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            {renderBreakdownTable(t('statusBreakdown'), statuses)}
            {renderBreakdownTable(t('movementBreakdown'), actions)}
            {renderBreakdownTable(t('releaseBreakdown'), releases)}
          </section>
        </>
      )}
    </AppLayout>
  );
};

export default SalesReportsView;
